import React, { useState } from "react";
import "./Audio.css";
import emailjs from "@emailjs/browser";
import Footer from "../../components/Footer/Footer";

// EmailJS constants (Audio)
const SERVICE_ID = "service_rnqp5dd";
const AUDIO_ADMIN_TEMPLATE_ID = "template_toyvi0m"; // Audio Form (admin)
const AUDIO_AUTOREPLY_TEMPLATE_ID = "template_erk7x78"; // Auto-Reply Audio Form
const PUBLIC_KEY = "QKlnswpeeF5706uvk";

const Audio = () => {
  const [sending, setSending] = useState(false);
  const [statusMessage, setStatusMessage] = useState("");

  const [formData, setFormData] = useState({
    name: "",
    organization: "",
    email: "",
    phone: "",
    eventType: "",
    eventDate: "",
    location: "",
    scope: [],
    venueSize: "",
    techNotes: "",
    bestTime: "",
    hp: "", // honeypot
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((s) => ({ ...s, [name]: value }));
  };

  const toggleScope = (e) => {
    const { value, checked } = e.target;
    setFormData((s) => {
      const set = new Set(s.scope);
      checked ? set.add(value) : set.delete(value);
      return { ...s, scope: Array.from(set) };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.hp) return; // bot trap

    setSending(true);
    setStatusMessage("");

    const payload = {
      name: formData.name,
      organization: formData.organization,
      email: formData.email,
      phone: formData.phone,
      event_type: formData.eventType,
      event_date: formData.eventDate,
      location: formData.location,
      scope: formData.scope.join(", "),
      venue_size: formData.venueSize,
      tech_notes: formData.techNotes,
      best_time: formData.bestTime,
      to_email: "ivan@melodiamusicllc.com", // admin recipient
    };

    try {
      // 1) Admin must succeed
      await emailjs.send(
        SERVICE_ID,
        AUDIO_ADMIN_TEMPLATE_ID,
        payload,
        PUBLIC_KEY
      );

      // 2) Auto-reply best-effort
      try {
        await emailjs.send(
          SERVICE_ID,
          AUDIO_AUTOREPLY_TEMPLATE_ID,
          { ...payload, to_email: formData.email }, // template uses {{to_email}}
          PUBLIC_KEY
        );
      } catch (autoErr) {
        console.warn("Audio auto-reply failed:", autoErr);
      }

      setStatusMessage(
        "Thanks! Your audio request was received. I’ll reach out to you shortly."
      );
      setFormData({
        name: "",
        organization: "",
        email: "",
        phone: "",
        eventType: "",
        eventDate: "",
        location: "",
        scope: [],
        venueSize: "",
        techNotes: "",
        bestTime: "",
        hp: "",
      });
    } catch (err) {
      setStatusMessage(
        "There was an error submitting the form. Please try again."
      );
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <section className="audio-section">
        <h2>Audio Services Inquiry</h2>
        <p className="audio-intro">
          Tell me about your service, conference, or event. I’ll review the
          details and get back to you.
        </p>

        <div className="audio-form-container">
          <form onSubmit={handleSubmit}>
            {/* Honeypot */}
            <input
              type="text"
              name="hp"
              value={formData.hp}
              onChange={handleChange}
              className="hp"
              aria-hidden="true"
            />

            <div className="grid-2">
              <input
                type="text"
                name="name"
                placeholder="Your Name"
                value={formData.name}
                onChange={handleChange}
                required
              />
              <input
                type="text"
                name="organization"
                placeholder="Organization / Church (optional)"
                value={formData.organization}
                onChange={handleChange}
              />
            </div>

            <div className="grid-2">
              <input
                type="email"
                name="email"
                placeholder="Email"
                value={formData.email}
                onChange={handleChange}
                required
              />
              <input
                type="tel"
                name="phone"
                placeholder="Phone"
                value={formData.phone}
                onChange={handleChange}
                required
              />
            </div>

            <div className="grid-2">
              <select
                name="eventType"
                value={formData.eventType}
                onChange={handleChange}
                required
              >
                <option value="">Event Type</option>
                <option value="Church Service">Church Service</option>
                <option value="Conference">Conference</option>
                <option value="Concert">Concert</option>
                <option value="Recording Session">Recording Session</option>
                <option value="Other">Other</option>
              </select>

              <input
                type="text"
                name="eventDate"
                placeholder="Date(s)"
                value={formData.eventDate}
                onChange={handleChange}
                required
              />
            </div>

            <input
              type="text"
              name="location"
              placeholder="City / State (or Remote)"
              value={formData.location}
              onChange={handleChange}
              required
            />

            <fieldset className="scope">
              <legend>Scope</legend>
              <label>
                <input
                  type="checkbox"
                  value="In-house Mix"
                  checked={formData.scope.includes("In-house Mix")}
                  onChange={toggleScope}
                />
                In-house Mix
              </label>
              <label>
                <input
                  type="checkbox"
                  value="Livestream Mix"
                  checked={formData.scope.includes("Livestream Mix")}
                  onChange={toggleScope}
                />
                Livestream Mix
              </label>
              <label>
                <input
                  type="checkbox"
                  value="System Setup & Tuning"
                  checked={formData.scope.includes("System Setup & Tuning")}
                  onChange={toggleScope}
                />
                System Setup &amp; Tuning
              </label>
              <label>
                <input
                  type="checkbox"
                  value="Recording"
                  checked={formData.scope.includes("Recording")}
                  onChange={toggleScope}
                />
                Recording
              </label>
            </fieldset>

            <select
              name="venueSize"
              value={formData.venueSize}
              onChange={handleChange}
            >
              <option value="">Room / Venue Size (optional)</option>
              <option value="Small (under 150)">Small (under 150)</option>
              <option value="Medium (150–500)">Medium (150–500)</option>
              <option value="Large (500+)">Large (500+)</option>
            </select>

            <textarea
              name="techNotes"
              rows="5"
              placeholder="Tech notes (console brand, channels/tracks, special requests, etc.)"
              value={formData.techNotes}
              onChange={handleChange}
            />

            <input
              type="text"
              name="bestTime"
              placeholder="Best time to reach you (e.g., weekdays after 5pm)"
              value={formData.bestTime}
              onChange={handleChange}
            />

            <button type="submit" disabled={sending}>
              {sending ? "Sending..." : "Submit Request"}
            </button>

            {statusMessage && <p className="status">{statusMessage}</p>}

            <p className="privacy">
              By submitting, you agree to be contacted about your request.
            </p>
          </form>
        </div>
      </section>

      <Footer />
    </>
  );
};

export default Audio;
